import React, { useEffect, useState, useRef } from "react";
import { useSupport } from "../context/SupportContext";
import { useAuth } from "../../context/AuthContext";
import { useUploader } from "@utils";
import { Card } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@shadcn-in-nx/ui/avatar";
import {
    Clock,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Send,
    User,
    Headphones,
    Paperclip,
    X,
    File,
    Image as ImageIcon,
    Film,
    FileText,
    Download,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const statusConfig = {
    OPEN: {
        label: "Open",
        color: "bg-blue-100 text-blue-800 border-blue-200",
        icon: AlertCircle,
    },
    IN_PROGRESS: {
        label: "In Progress",
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        icon: Clock,
    },
    WAITING_ON_CUSTOMER: {
        label: "Awaiting Your Reply",
        color: "bg-orange-100 text-orange-800 border-orange-200",
        icon: Clock,
    },
    RESOLVED: {
        label: "Resolved",
        color: "bg-green-100 text-green-800 border-green-200",
        icon: CheckCircle2,
    },
    CLOSED: {
        label: "Closed",
        color: "bg-gray-100 text-gray-800 border-gray-200",
        icon: CheckCircle2,
    },
};

const priorityConfig = {
    LOW: { label: "Low", color: "text-gray-500" },
    NORMAL: { label: "Normal", color: "text-blue-500" },
    HIGH: { label: "High", color: "text-orange-500" },
    URGENT: { label: "Urgent", color: "text-red-500" },
};

// Helper to get file icon based on type
const getFileIcon = (type) => {
    if (type?.startsWith("image/")) return ImageIcon;
    if (type?.startsWith("video/")) return Film;
    if (type?.includes("pdf")) return FileText;
    return File;
};

// Helper to format file size
const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function TicketDetail({ ticketId }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const {
        currentTicket,
        isLoadingTicket,
        isAddingReply,
        getTicket,
        addReply,
        clearCurrentTicket,
    } = useSupport();

    const [replyMessage, setReplyMessage] = useState("");
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    // File uploader
    const {
        loading: isUploading,
        files: uploadedFiles,
        uploadFiles,
        removeFile,
        clearFiles,
    } = useUploader({ isPublic: true });

    // Fetch ticket on mount and record view
    useEffect(() => {
        getTicket(ticketId);
        try {
            const views = JSON.parse(localStorage.getItem("support_ticket_views") || "{}");
            views[ticketId] = new Date().toISOString();
            localStorage.setItem("support_ticket_views", JSON.stringify(views));
            localStorage.setItem("support_last_viewed_ticket", ticketId);
        } catch {}
        return () => clearCurrentTicket();
    }, [ticketId, getTicket, clearCurrentTicket]);

    // Scroll to bottom of messages when new message added
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [currentTicket?.messages]);

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files?.length) {
            uploadFiles(files);
        }
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSendReply = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim() && uploadedFiles.filter(f => f.status === "completed").length === 0) return;

        try {
            // Format attachments from uploaded files
            const attachments = uploadedFiles
                .filter(f => f.status === "completed")
                .map(f => ({
                    url: f.permanentUrl || f.url,
                    name: f.name,
                    type: f.file?.type,
                    size: f.file?.size,
                }));

            await addReply(ticketId, replyMessage.trim(), attachments);
            setReplyMessage("");
            clearFiles();
            toast.success(t("support.ticketDetail.replySent"));
        } catch (error) {
            toast.error(t("support.ticketDetail.replyFailed"));
        }
    };

    if (isLoadingTicket || !currentTicket) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const status = statusConfig[currentTicket.status] || statusConfig.OPEN;
    const priority = priorityConfig[currentTicket.priority] || priorityConfig.NORMAL;
    const StatusIcon = status.icon;
    const isClosed = currentTicket.status === "CLOSED";

    return (
        <div className="space-y-6">
            {/* Ticket Header */}
            <Card className="p-6 border-2 border-border/50">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                            <Badge
                                variant="outline"
                                className={`${status.color} font-medium`}
                            >
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {status.label}
                            </Badge>
                            {currentTicket.priority && currentTicket.priority !== "NORMAL" && (
                                <Badge variant="outline" className={priority.color}>
                                    {priority.label} Priority
                                </Badge>
                            )}
                        </div>
                        <h1 className="text-xl md:text-2xl font-bold mb-2">
                            {currentTicket.subject}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <span>Ticket #{currentTicket.id.slice(-8)}</span>
                            <span>
                                Created {format(new Date(currentTicket.createdAt), "MMM d, yyyy 'at' h:mm a")}
                            </span>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Messages */}
            <Card className="border-2 border-border/50 overflow-hidden">
                <div className="max-h-[500px] overflow-y-auto p-4 md:p-6 space-y-4">
                    {currentTicket.messages?.map((message, index) => {
                        const isStaff = message.isInternal || message.sender?.roles?.some(
                            r => r === "admin" || r === "super-admin" || r === "support"
                        );
                        const isOwnMessage = message.sender?.id === user?.id;
                        const senderInitials = (message.sender?.firstName?.charAt(0) || "") + (message.sender?.lastName?.charAt(0) || "");

                        return (
                            <div
                                key={message.id}
                                className={`flex ${isOwnMessage && !isStaff ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={`max-w-[80%] ${
                                        isStaff
                                            ? "bg-blue-50 border border-blue-100"
                                            : isOwnMessage
                                            ? "bg-gray-100 border border-gray-200"
                                            : "bg-white border border-gray-200"
                                    } rounded-lg p-4`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <Avatar className="w-8 h-8">
                                            {message.sender?.profilePicture ? (
                                                <AvatarImage src={message.sender.profilePicture} alt={message.sender?.firstName} />
                                            ) : null}
                                            <AvatarFallback className={isStaff ? "bg-blue-200 text-blue-700" : "bg-gray-200 text-gray-600"}>
                                                {isStaff ? (
                                                    <Headphones className="w-4 h-4" />
                                                ) : (
                                                    senderInitials || <User className="w-4 h-4" />
                                                )}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="text-sm font-medium">
                                                {isStaff
                                                    ? t("support.ticketDetail.supportTeam")
                                                    : message.sender?.firstName
                                                    ? `${message.sender.firstName} ${message.sender.lastName || ""}`
                                                    : t("support.ticketDetail.you")}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {formatDistanceToNow(new Date(message.createdAt), {
                                                    addSuffix: true,
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                    {message.content && (
                                        <div className="text-sm whitespace-pre-wrap break-words">
                                            {message.content}
                                        </div>
                                    )}
                                    {/* Attachments */}
                                    {message.attachments?.length > 0 && (
                                        <div className="mt-3 space-y-2">
                                            {message.attachments.map((attachment, i) => {
                                                const FileIcon = getFileIcon(attachment.type);
                                                const isImage = attachment.type?.startsWith("image/");

                                                return (
                                                    <div key={i}>
                                                        {isImage ? (
                                                            <a
                                                                href={attachment.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="block"
                                                            >
                                                                <img
                                                                    src={attachment.url}
                                                                    alt={attachment.name}
                                                                    className="max-w-full max-h-64 rounded-lg border border-border/50 cursor-pointer hover:opacity-90 transition-opacity"
                                                                />
                                                            </a>
                                                        ) : (
                                                            <a
                                                                href={attachment.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex items-center gap-2 p-2 bg-white/50 rounded-lg border border-border/50 hover:bg-white/80 transition-colors"
                                                            >
                                                                <FileIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                                <span className="text-sm truncate flex-1">{attachment.name}</span>
                                                                {attachment.size && (
                                                                    <span className="text-xs text-muted-foreground">{formatFileSize(attachment.size)}</span>
                                                                )}
                                                                <Download className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                            </a>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>

                {/* Reply Form */}
                {!isClosed && (
                    <div className="border-t border-border p-4 md:p-6 bg-muted/30">
                        <form onSubmit={handleSendReply}>
                            <div className="space-y-4">
                                <Textarea
                                    placeholder={t("support.ticketDetail.typeReply")}
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    rows={3}
                                    className="resize-none"
                                    disabled={isAddingReply || isUploading}
                                />

                                {/* Uploaded files preview */}
                                {uploadedFiles.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {uploadedFiles.map((file) => {
                                            const FileIcon = getFileIcon(file.file?.type);
                                            const isImage = file.file?.type?.startsWith("image/");

                                            return (
                                                <div
                                                    key={file.id}
                                                    className="relative group"
                                                >
                                                    {isImage && file.status === "completed" ? (
                                                        <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-border/50">
                                                            <img
                                                                src={file.permanentUrl || file.url || URL.createObjectURL(file.file)}
                                                                alt={file.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => removeFile(file.id)}
                                                                className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                            >
                                                                <X className="w-3 h-3 text-white" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-border/50">
                                                            {file.status === "uploading" ? (
                                                                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                                            ) : file.status === "error" ? (
                                                                <AlertCircle className="w-4 h-4 text-red-500" />
                                                            ) : (
                                                                <FileIcon className="w-4 h-4 text-muted-foreground" />
                                                            )}
                                                            <span className="text-sm truncate max-w-[120px]">{file.name}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeFile(file.id)}
                                                                className="w-4 h-4 flex items-center justify-center hover:bg-gray-100 rounded"
                                                            >
                                                                <X className="w-3 h-3 text-muted-foreground" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            onChange={handleFileSelect}
                                            className="hidden"
                                            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isAddingReply || isUploading}
                                        >
                                            <Paperclip className="w-4 h-4 mr-2" />
                                            {t("support.ticketDetail.attachFile")}
                                        </Button>
                                        <p className="text-xs text-muted-foreground hidden sm:block">
                                            {t("support.ticketDetail.maxFileSize")}
                                        </p>
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={(!replyMessage.trim() && uploadedFiles.filter(f => f.status === "completed").length === 0) || isAddingReply || isUploading}
                                        className="bg-gray-900 hover:bg-gray-800 text-white"
                                    >
                                        {isAddingReply ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        {isAddingReply ? t("support.ticketDetail.sending") : t("support.ticketDetail.sendReply")}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* Closed Notice */}
                {isClosed && (
                    <div className="border-t border-border p-4 md:p-6 bg-muted/50 text-center">
                        <p className="text-sm text-muted-foreground">
                            This ticket is closed. Create a new ticket if you need further assistance.
                        </p>
                    </div>
                )}
            </Card>
        </div>
    );
}
